/**
 * List attribution: remembers which list a product was clicked from and carries
 * that context onto the later events for the same product.
 *
 * Entries are keyed by variant AND by product: a card click knows the card's
 * variant, but the visitor may switch variants on the PDP before adding to
 * cart, in which case the product-level entry still resolves.
 *
 * Storage is localStorage (with a TTL) rather than sessionStorage because the
 * Shopify cart outlives the browser session so a visitor returning the next day
 * would otherwise see an unattributed view_cart. Falls back to sessionStorage,
 * then to an in-memory map, when storage is unavailable (Safari private mode).
 */
import { readJSON } from '@theme/analytics-utils';

const STORAGE_KEY = 'jm:listAttribution';
/** Matches the practical lifetime of a Shopify cart. */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Cap the store so a long browsing session can't grow it without bound. */
const MAX_ENTRIES = 100;

/** @type {Record<string, any>} used when neither localStorage nor sessionStorage works */
let memoryStore = {};

/** @returns {Storage|null} */
function storage() {
  for (const name of ['localStorage', 'sessionStorage']) {
    try {
      const store = window[name];
      const probe = '__jm_probe__';
      store.setItem(probe, '1');
      store.removeItem(probe);
      return store;
    } catch (error) {
      /* blocked or full — try the next one */
    }
  }
  return null;
}

/** @returns {Record<string, any>} */
function readStore() {
  const store = storage();
  if (!store) return memoryStore;
  try {
    return JSON.parse(store.getItem(STORAGE_KEY) || '{}') || {};
  } catch (error) {
    return {};
  }
}

/**
 * Drop expired entries, trim to the newest MAX_ENTRIES, persist.
 * @param {Record<string, any>} entries
 */
function writeStore(entries) {
  const cutoff = Date.now() - TTL_MS;
  let keys = Object.keys(entries).filter((key) => Number(entries[key] && entries[key].ts) > cutoff);

  if (keys.length > MAX_ENTRIES) {
    keys.sort((a, b) => Number(entries[b].ts) - Number(entries[a].ts));
    keys = keys.slice(0, MAX_ENTRIES);
  }

  const next = {};
  for (const key of keys) next[key] = entries[key];

  memoryStore = next;
  const store = storage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    /* quota — the in-memory copy still serves this page load */
  }
}

/**
 * Look up the list context recorded for a product, preferring the exact variant.
 * @param {string|number} [productId]
 * @param {string|number} [variantId]
 * @returns {{list_name?: string, list_id?: string, index?: number, origin?: string}|null}
 */
export function lookup(productId, variantId) {
  const entries = readStore();
  const cutoff = Date.now() - TTL_MS;
  const candidates = [];
  if (variantId != null && variantId !== '') candidates.push(`v:${variantId}`);
  if (productId != null && productId !== '') candidates.push(`p:${productId}`);

  for (const key of candidates) {
    const entry = entries[key];
    if (entry && Number(entry.ts) > cutoff) return entry;
  }
  return null;
}

/**
 * Attach item_list_name / item_list_id / index to an item, when the product's
 * list of origin is known. Mutates and returns the item.
 * @param {Record<string, any>} item
 * @param {string|number} [productId]
 * @param {string|number} [variantId]
 * @returns {Record<string, any>}
 */
export function applyAttribution(item, productId, variantId) {
  const entry = lookup(productId, variantId);
  if (!entry) return item;
  if (entry.list_name && item.item_list_name == null) item.item_list_name = entry.list_name;
  if (entry.list_id && item.item_list_id == null) item.item_list_id = entry.list_id;
  if (entry.index && item.index == null) item.index = entry.index;
  return item;
}

/**
 * The `origin` for view_item: where the visitor came from. 'direct' when the PDP
 * was not reached through a tracked list (deep link, search engine, bookmark).
 * @param {string|number} [productId]
 * @param {string|number} [variantId]
 * @returns {string}
 */
export function originFor(productId, variantId) {
  const entry = lookup(productId, variantId);
  return (entry && entry.origin) || 'direct';
}

/**
 * Fallback origin for lists that don't declare `data-analytics-list-origin`,
 * derived from the page the click happened on.
 * @returns {string}
 */
function originFromPage() {
  const pageType = readJSON('analytics-page').page_type;
  switch (pageType) {
    case 'product_listing_page':
      return 'category';
    case 'homepage':
      return 'homepage';
    case 'product_detail_page':
      return 'recommendation';
    case 'cart':
      return 'cart';
    case 'content':
      return 'content';
    default:
      return 'other';
  }
}

/**
 * Record the list context for a clicked card.
 * @param {Element} container the [data-analytics-list] element
 * @param {Element} card the product card element
 */
function record(container, card) {
  // The card's own item JSON is what makes it a card. Requiring it also rules
  // out false positives: some list containers carry a data-product-id of their
  // own (product recommendations reference the PDP product they belong to).
  const script = card.querySelector('script.analytics-item[data-analytics-context="card"]');
  if (!script) return;

  const productId = script.getAttribute('data-product-id') || card.getAttribute('data-product-id') || '';
  const variantId = script.getAttribute('data-variant-id') || '';
  if (!productId && !variantId) return;

  // Position within the list, numbered exactly as analytics-lists.js numbers it
  // for view_item_list, so index is consistent across the two events.
  let index;
  const nodes = container.querySelectorAll('script.analytics-item[data-analytics-context="card"]');
  const position = Array.prototype.indexOf.call(nodes, script);
  if (position !== -1) index = position + 1;

  const entry = {
    list_name: container.getAttribute('data-analytics-list-name') || '',
    list_id: container.getAttribute('data-analytics-list-id') || '',
    origin: container.getAttribute('data-analytics-list-origin') || originFromPage(),
    index,
    ts: Date.now(),
  };

  const entries = readStore();
  if (productId) entries[`p:${productId}`] = entry;
  if (variantId) entries[`v:${variantId}`] = entry;
  writeStore(entries);
}

/** @param {MouseEvent} event */
function onClick(event) {
  const target = /** @type {Element} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;

  const container = target.closest('[data-analytics-list]');
  if (!container) return;

  // `closest('product-card')` skips nested elements that also carry a
  // data-product-id (the quick-add form), which would hide the card's item JSON.
  const card = target.closest('product-card') || target.closest('[data-product-id]');
  if (!card || card === container || !container.contains(card)) return;

  record(container, card);
}

document.addEventListener('click', onClick, { capture: true });
